/**
 * 「实际用时」的显示口径 —— ✅ **全项目只有这一处**。
 *
 * ## 为什么必须收敛成一处
 *
 * 用户原话：「不到 1 分钟显示秒」。在这之前，界面上"秒 → 人话"至少有四份实现，
 * 而且口径互不相同：
 *
 *   | 实现 | 原来的写法 | 病 |
 *   | --- | --- | --- |
 *   | 本文件（当时在 `time.ts`） | `'1小时23分'` | ≥ 60 秒就把秒丢掉 |
 *   | `apps/web/src/lib/utils.ts` 的 `formatMinutesText` | `'25 分钟'` | **四舍五入到分钟** |
 *   | `apps/web/.../admin/AdminBits.tsx` 的 `formatDuration` | `'5 天 3 小时'` | 同名不同口径 |
 *   | `homework-overdue.ts` 的 `formatOverdueDuration` | `'不到 1 分钟'` | 超时专用（保留，见下） |
 *
 * 于是 **45 秒的真实用时被显示成「0 分钟」**，用户据此以为"这个应用根本没有
 * 记录完成时间"——真实数据没丢，是**显示**丢了。同一个概念在多处各写一份，
 * 改一处漏一处，这个项目已经反复因此踩坑（见 `homework-draft.ts` 的模块头注释）。
 *
 * ## 谁该用、谁不该用
 *
 * - ✅ **实际用时**（服务端的 `checkin.actualSeconds`）→ 用本函数。
 * - ⛔ **目标 / 计划时长**（`estimatedSeconds`、`durationMinutes`、
 *   "每天阅读 30 分钟"这类**配置值**）→ 不要用本函数：它们是"打算做多久"，
 *   不是"真的做了多久"，显示到分钟就够（见 `apps/web/src/lib/utils.ts` 的
 *   `formatPlannedDuration`）。把两者混起来是这个需求最容易犯的错。
 * - ⛔ **超时多久**（`overdueSeconds`）→ 用 `homework-overdue.ts` 的
 *   `formatOverdueDuration`：那是"欠了多久"，不是"投入了多久"，
 *   它的口径（四舍五入到分钟 + `'不到 1 分钟'`）刻意与用时不同。
 *
 * ## 口径
 *
 * | 入参 | 输出 |
 * | --- | --- |
 * | `0`（真实值：确实一秒都没走过） | `'0 秒'` |
 * | `1` / `30` / `45` / `59` | `'1 秒'` / `'30 秒'` / `'45 秒'` / `'59 秒'` |
 * | `60` / `80` / `1163` | `'1 分 0 秒'` / `'1 分 20 秒'` / `'19 分 23 秒'` |
 * | `3599` | `'59 分 59 秒'` |
 * | `3600` / `3661` | `'1 小时 0 分 0 秒'` / `'1 小时 1 分 1 秒'` |
 * | `null` / `undefined` / 负数 / `NaN` / `Infinity` | `'—'` |
 *
 * **为什么 ≥ 60 秒也保留秒**：用户这次的诉求就是"别把真实时间显示丢"。19 分 23 秒
 * 是孩子的真实投入，写成「19 分钟」丢掉的 23 秒看着小，但只要允许"为了好看丢精度"，
 * "45 秒 → 0 分钟"那次事故就会换个地方重演。同理，`'1 分 0 秒'` 里的 `0` 也照写：
 * 单位补全后位数稳定，家长扫一眼就能比较，不用猜"这个 0 是没显示还是真的是 0"。
 *
 * **为什么 `0` 写「0 秒」而不是「未计时」**：`0` 与 `null` 在本项目里是**两个事实**
 * （见 `apps/web/src/lib/api/types.ts` 里 `actualSeconds` 的注释：`null` = "这次响应
 * 没说"，`0` = "服务端说确实没走过时间"）。把 `0` 说成"未计时"等于替服务端**编一句话**
 * —— 这个项目刚在积分与计时上各修过一次同类错误，不要再犯。
 *
 * **为什么负数给「—」而不是夹到 `0 秒`**：负数不是"零"，是"这个数不该出现"。
 * 悄悄夹到 0 会让上游的 bug（例如时区算错、字段读错）永远看不见。
 *
 * ⚠️ 本函数只回答"**怎么写**"，不回答"**要不要写**"：调用方自己决定这一行要不要
 * 显示用时（现在各处用 `actualSeconds > 0`）。与 `formatOverdueDuration()` 同一套分工。
 *
 * 纯函数：无 React / DOM / 环境依赖，前端与后端（`apps/api`）都能直接用。
 */

/** 无法显示成用时（"这次没说"或非法值）时的占位符。 */
export const DURATION_PLACEHOLDER = '—';

/**
 * 秒 → 实际用时的人话：`'45 秒'` / `'1 分 20 秒'` / `'1 小时 1 分 1 秒'`。
 *
 * @param seconds 服务端给的秒数；`null` / `undefined` / 负数 / 非有限值 → `'—'`
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
    return DURATION_PLACEHOLDER;
  }
  const total = Math.floor(seconds);
  const s = total % 60;
  const totalMinutes = Math.floor(total / 60);
  if (totalMinutes === 0) return `${s} 秒`;
  const m = totalMinutes % 60;
  const h = Math.floor(totalMinutes / 60);
  return h === 0 ? `${m} 分 ${s} 秒` : `${h} 小时 ${m} 分 ${s} 秒`;
}
